import React from 'react';
import type { Tone } from '../../domain/types';

export interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: number;
  progress?: number;
  max?: number;
  tone?: Tone;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  label?: string;
  animated?: boolean;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  progress,
  max = 100,
  tone = 'accent',
  size = 'md',
  showLabel = false,
  label,
  animated = true,
  className = '',
  style,
  ...props
}) => {
  const actualValue = progress !== undefined ? progress : value !== undefined ? value : 0;
  const clampedValue = Math.min(Math.max(0, actualValue), max);
  const percentage = Math.round((clampedValue / max) * 100);

  const getToneColor = (t: Tone) => {
    switch (t) {
      case 'ok':
        return 'var(--z-ok)';
      case 'warn':
        return 'var(--z-warn)';
      case 'bad':
        return 'var(--z-bad)';
      case 'flat':
        return 'var(--z-text-2)';
      case 'accent':
      default:
        return 'var(--z-accent)';
    }
  };

  const heightBySize: Record<'sm' | 'md' | 'lg', number> = {
    sm: 4,
    md: 6,
    lg: 10
  };

  const barHeight = heightBySize[size] || heightBySize.md;
  const barColor = getToneColor(tone);

  return (
    <div
      className={`z-progress-container ${className}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
        ...style
      }}
      {...props}
    >
      {(showLabel || label) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '11px',
            color: 'var(--z-dim)'
          }}
        >
          <span>{label || 'Avance'}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{percentage}%</span>
        </div>
      )}

      <div
        role="progressbar"
        aria-valuenow={clampedValue}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuetext={`${percentage}%`}
        aria-label={label || 'Progreso de la tarea'}

        style={{
          width: '100%',
          height: `${barHeight}px`,
          backgroundColor: 'var(--z-fill-2)',
          borderRadius: '9999px',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: barColor,
            borderRadius: '9999px',
            transition: animated ? 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
          }}
        />
      </div>
    </div>
  );
};
